import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { GiHamburgerMenu } from 'react-icons/gi';
import { FaCommentDots } from 'react-icons/fa';
import {
  FiSettings,
  FiLock,
  FiChevronDown,
  FiLogOut,
  FiHome,
  FiInfo,
  FiHelpCircle,
  FiAward,
  FiBook,
  FiFileText,
} from 'react-icons/fi';
import { IoHeadset } from 'react-icons/io5';
import { IoMdClose } from 'react-icons/io';
import { getAccessibleUrl } from 'utils/files';

import WrapperLink from 'components/WrapperLink';

import {
  Avatar,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  useDisclosure,
  useBreakpointValue,
} from '@chakra-ui/react';
import { ROUTES } from 'constants/routes';
import { UserAPI, IUser } from 'API/user';
import FirstTutorsBanner from 'components/FirstTutorsBanner';

import styles from './Navbar.module.css';

function Navbar() {
  const router = useRouter();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [currentUser, setCurrentUser] = useState<IUser | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    UserAPI.userData()
      .then((user) => {
        setCurrentUser(user);
        if (router.pathname === '/' && user.role === 'TUTOR') {
          router.replace('/tutor/dashboard');
        }
      })
      .catch(() => {}); // not logged in, ignore
  }, [router]);

  useEffect(() => {
    if (currentUser?.profile_url) {
      getAccessibleUrl(currentUser.profile_url).then((url) =>
        setProfileImageUrl(url)
      );
    } else {
      setProfileImageUrl(null);
    }
  }, [currentUser?.profile_url]);

  // Close dropdown on click outside
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const isAdmin =
    currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPER_ADMIN';

  const profileHref =
    currentUser?.role === 'TUTOR'
      ? '/tutor/dashboard'
      : isAdmin
      ? '/admin'
      : '/student/settings';

  const isStudent = currentUser?.role === 'STUDENT';

  const roleLabel =
    currentUser?.role === 'TUTOR' ? 'Tutor' : isAdmin ? 'Admin' : 'Student';

  const roleBadgeClass =
    currentUser?.role === 'TUTOR'
      ? styles.roleBadgeTutor
      : isAdmin
      ? styles.roleBadgeAdmin
      : styles.roleBadgeStudent;

  const handleSignOut = () => {
    UserAPI.signout()
      .then(() => {
        setCurrentUser(null);
        router.push('/');
      })
      .catch(() => {});
  };

  return (
    <>
      <div role="banner" className={`${styles.header} ${styles['w-nav']}`}>
        <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
          <DrawerOverlay />
          <DrawerContent className={styles.drawer}>
            <DrawerBody p={0}>
              {/* Header — logo + close */}
              <div className={styles.drawerHeader}>
                <Image
                  width={140}
                  height={28}
                  src={'/navbar/logo.png'}
                  alt="logo"
                  className={styles.drawerLogo}
                />
                <button className={styles.drawerCloseBtn} onClick={onClose}>
                  <IoMdClose size={16} />
                </button>
              </div>

              {/* User identity row */}
              {currentUser && (
                <div className={styles.drawerUserRow}>
                  <div className={styles.drawerUserAvatar}>
                    {profileImageUrl ? (
                      <img
                        src={profileImageUrl}
                        alt="avatar"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      `${currentUser.first_name?.[0] || ''}${
                        currentUser.last_name?.[0] || ''
                      }`
                    )}
                  </div>
                  <div>
                    <div className={styles.drawerUserName}>
                      {currentUser.first_name} {currentUser.last_name}
                    </div>
                    <span className={`${styles.roleBadge} ${roleBadgeClass}`}>
                      {roleLabel}
                    </span>
                  </div>
                </div>
              )}

              <div className={styles.drawerBody}>
                {/* Public nav */}
                <div className={styles.drawerSectionLabel}>Navigate</div>
                {[
                  { title: 'Home', href: '/', icon: <FiHome size={18} /> },
                  {
                    title: 'About',
                    href: '/about',
                    icon: <FiInfo size={18} />,
                  },
                  {
                    title: "FAQ's",
                    href: '/faqs',
                    icon: <FiHelpCircle size={18} />,
                  },
                  {
                    title: 'Free Resources',
                    href: '/free-resources',
                    icon: <FiBook size={18} />,
                  },
                  {
                    title: 'UCAS Coaching',
                    href: ROUTES.ucasCoaching,
                    icon: <FiAward size={18} />,
                    isNew: true,
                  },
                  {
                    title: 'Blog',
                    href: '/blog',
                    icon: <FiFileText size={18} />,
                  },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className={styles.drawerItem}
                    onClick={onClose}
                  >
                    <span className={styles.drawerItemIcon}>{link.icon}</span>
                    {link.title}
                    {link.isNew && <span className={styles.newPill}>NEW</span>}
                  </a>
                ))}

                {currentUser && (
                  <>
                    <div className={styles.drawerDivider} />

                    {/* Authenticated section */}
                    <div className={styles.drawerSectionLabel}>My account</div>
                    {isStudent ? (
                      <>
                        <Link href="/student/chat" passHref>
                          <a className={styles.drawerItem} onClick={onClose}>
                            <span className={styles.drawerItemIcon}>
                              <FaCommentDots size={16} />
                            </span>
                            Chat
                          </a>
                        </Link>
                        <Link href="/student/settings" passHref>
                          <a className={styles.drawerItem} onClick={onClose}>
                            <span className={styles.drawerItemIcon}>
                              <FiSettings size={18} />
                            </span>
                            Settings
                          </a>
                        </Link>
                        <Link href="/student/login-details" passHref>
                          <a className={styles.drawerItem} onClick={onClose}>
                            <span className={styles.drawerItemIcon}>
                              <FiLock size={18} />
                            </span>
                            Login details
                          </a>
                        </Link>
                        <Link href="/student/customer-support" passHref>
                          <a className={styles.drawerItem} onClick={onClose}>
                            <span className={styles.drawerItemIcon}>
                              <IoHeadset size={18} />
                            </span>
                            Customer support
                          </a>
                        </Link>
                      </>
                    ) : (
                      <a
                        href={profileHref}
                        className={styles.drawerItem}
                        onClick={onClose}
                      >
                        <span className={styles.drawerItemIcon}>
                          <FiHome size={18} />
                        </span>
                        My Profile
                      </a>
                    )}
                  </>
                )}

                {!currentUser && (
                  <>
                    <div className={styles.drawerDivider} />
                    <div className={styles.drawerSectionLabel}>Portals</div>
                    <a
                      href={ROUTES.tutorLogin}
                      className={styles.drawerItem}
                      onClick={onClose}
                    >
                      <span className={styles.drawerItemIcon}>
                        <FaCommentDots size={16} />
                      </span>
                      For Tutors
                    </a>
                  </>
                )}
              </div>

              {/* Footer */}
              {currentUser ? (
                <div className={styles.drawerFooter}>
                  <button
                    className={`${styles.drawerItem} ${styles.drawerItemDanger}`}
                    onClick={() => {
                      handleSignOut();
                      onClose();
                    }}
                  >
                    <span className={styles.drawerItemIcon}>
                      <FiLogOut size={18} />
                    </span>
                    Sign out
                  </button>
                </div>
              ) : (
                <div className={styles.drawerFooter}>
                  <a
                    href={ROUTES.studentLogin}
                    className={styles.drawerBtnPrimary}
                    onClick={onClose}
                    style={{
                      display: 'block',
                      margin: '16px auto 6px',
                      width: 'fit-content',
                      padding: '12px 40px',
                      borderRadius: '6px',
                      background: '#079ba9',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 600,
                      textAlign: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    Sign In
                  </a>
                </div>
              )}
            </DrawerBody>
          </DrawerContent>
        </Drawer>
        <div
          className={`${styles['container-default']} ${styles['header-container']} ${styles['w-container']}`}
        >
          <div
            className={styles['header-wrapper']}
            style={
              isMobile
                ? { position: 'relative', justifyContent: 'center' }
                : undefined
            }
          >
            <div
              className={`${styles['split-content']} ${styles['header-right']}`}
            >
              <WrapperLink
                href="/"
                className={`${styles.brand} ${styles['w-nav-brand']} ${styles['w--current']}`}
              >
                <Image
                  width={200}
                  height={40}
                  src={'/navbar/logo.png'}
                  alt="logo"
                  className={styles['header-logo']}
                />
              </WrapperLink>
            </div>

            {!(isMobile && isStudent) && <DesktopNavLinks />}
            {isMobile ? (
              <IconButton
                aria-label="menu"
                alignContent={'center'}
                // eslint-disable-next-line react/no-children-prop
                children={<GiHamburgerMenu />}
                display={['flex', 'flex', 'none']}
                onClick={onOpen}
                position="absolute"
                left="10px"
                top="50%"
                transform="translateY(-50%)"
                zIndex={1}
              />
            ) : (
              <div className={styles.buttonsContainer}>
                {currentUser ? (
                  isStudent ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                    >
                      {/* Vertical divider */}
                      <div
                        style={{
                          width: '0.5px',
                          height: '20px',
                          background: '#ECECEC',
                        }}
                      />

                      {/* Chat icon button */}
                      <Link href="/student/chat" passHref>
                        <a
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '36px',
                            height: '36px',
                            border: '0.5px solid #ECECEC',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#666',
                            textDecoration: 'none',
                            transition: 'background 0.2s',
                          }}
                          onMouseEnter={(
                            e: React.MouseEvent<HTMLAnchorElement>
                          ) => (e.currentTarget.style.background = '#f7f7f7')}
                          onMouseLeave={(
                            e: React.MouseEvent<HTMLAnchorElement>
                          ) =>
                            (e.currentTarget.style.background = 'transparent')
                          }
                        >
                          <FaCommentDots size={18} />
                        </a>
                      </Link>

                      {/* Profile dropdown trigger */}
                      <div ref={dropdownRef} style={{ position: 'relative' }}>
                        <div
                          onClick={() => setDropdownOpen((prev) => !prev)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            padding: '5px 10px',
                            borderRadius: '6px',
                            border: '0.5px solid #ECECEC',
                          }}
                        >
                          <div
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: '#e8e7f8',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#2d2a8e',
                              fontSize: '12px',
                              fontWeight: 600,
                              overflow: 'hidden',
                              flexShrink: 0,
                            }}
                          >
                            {profileImageUrl ? (
                              /* eslint-disable-next-line @next/next/no-img-element */
                              <img
                                src={profileImageUrl}
                                alt="avatar"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                              />
                            ) : (
                              `${currentUser.first_name?.[0] || ''}${
                                currentUser.last_name?.[0] || ''
                              }`
                            )}
                          </div>
                          <span
                            style={{
                              fontSize: '13px',
                              fontWeight: 500,
                              color: '#333',
                            }}
                          >
                            {currentUser.first_name}
                          </span>
                          <FiChevronDown
                            size={14}
                            color="#666"
                            style={{
                              transition: 'transform 0.2s',
                              transform: dropdownOpen
                                ? 'rotate(180deg)'
                                : 'rotate(0)',
                            }}
                          />
                        </div>

                        {/* Dropdown menu */}
                        {dropdownOpen && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 'calc(100% + 8px)',
                              right: 0,
                              width: '220px',
                              background: 'white',
                              border: '0.5px solid #ECECEC',
                              borderRadius: '10px',
                              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                              zIndex: 100,
                              padding: '0',
                            }}
                          >
                            {/* Header */}
                            <div
                              style={{
                                padding: '12px 14px 10px',
                                borderBottom: '0.5px solid #ECECEC',
                              }}
                            >
                              <div
                                style={{
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  color: '#1a1a2e',
                                }}
                              >
                                {currentUser.first_name} {currentUser.last_name}
                              </div>
                              <div
                                style={{
                                  fontSize: '11px',
                                  color: '#999',
                                  marginTop: '2px',
                                }}
                              >
                                {currentUser.email}
                              </div>
                            </div>

                            {/* Menu items */}
                            <DropdownMenuItem
                              icon={<FiSettings size={16} />}
                              label="Settings"
                              href="/student/settings"
                              onClick={() => setDropdownOpen(false)}
                            />
                            <DropdownMenuItem
                              icon={<FiLock size={16} />}
                              label="Login details"
                              href="/student/login-details"
                              onClick={() => setDropdownOpen(false)}
                            />
                            <DropdownMenuItem
                              icon={<IoHeadset size={16} />}
                              label="Customer support"
                              href="/student/customer-support"
                              onClick={() => setDropdownOpen(false)}
                              showDivider
                            />

                            {/* Log out */}
                            <DropdownMenuItem
                              icon={<FiLogOut size={16} />}
                              label="Sign out"
                              onClick={() => {
                                setDropdownOpen(false);
                                handleSignOut();
                              }}
                              isLogout
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <a
                      href={profileHref}
                      className={styles.userPill}
                      style={{ textDecoration: 'none' }}
                    >
                      <Avatar size="sm" bg="var(--cadet-blue)" color="white" />
                      <span>{currentUser.first_name}</span>
                      <span className={`${styles.roleBadge} ${roleBadgeClass}`}>
                        {roleLabel}
                      </span>
                    </a>
                  )
                ) : (
                  <>
                    <a
                      href={ROUTES.tutorLogin}
                      className={`${styles.navBtn} ${styles.navBtnPrimary}`}
                    >
                      For Tutors
                    </a>
                    <a
                      href={ROUTES.studentLogin}
                      className={`${styles.navBtn} ${styles.navBtnOutline}`}
                    >
                      Sign In
                    </a>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Hide the migration banner on the migration page itself */}
      {router.pathname !== '/tutor-migration' && <FirstTutorsBanner />}
    </>
  );
}

const DesktopNavLinks = () => {
  const { pathname } = useRouter();
  const navLinks = [
    { title: 'Home', href: '/' },
    { title: 'About', href: '/about' },
    { title: "FAQ's", href: '/faqs' },
    { title: 'Blog', href: '/blog' },
    { title: 'Free Resources', href: '/free-resources' },
    { title: 'UCAS Coaching', href: ROUTES.ucasCoaching, isNew: true },
  ];

  return (
    <nav
      role="navigation"
      className={`${styles['nav-menu']} ${styles['w-nav-menu']}`}
      data-collapse="medium"
    >
      <ul role="list" className={styles['header-navigation']}>
        {navLinks.map((link, index) => (
          <li key={index} className={styles['nav-item-wrapper']}>
            <WrapperLink
              href={link.href}
              aria-current="page"
              className={`${styles['nav-link']} ${styles['w--current']} ${
                pathname === link.href ? styles.navLinkActive : ''
              }`}
            >
              {link.title}
              {link.isNew && <span className={styles.newPill}>NEW</span>}
            </WrapperLink>
          </li>
        ))}
      </ul>
    </nav>
  );
};

/* Dropdown menu item used in the student profile dropdown */
const DropdownMenuItem = ({
  icon,
  label,
  href,
  onClick,
  isLogout = false,
  showDivider = false,
}: {
  icon: React.ReactNode;
  label: string;
  href?: string;
  onClick?: () => void;
  isLogout?: boolean;
  showDivider?: boolean;
}) => {
  const itemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 10px',
    borderRadius: '6px',
    fontSize: '13px',
    color: isLogout ? '#dc2626' : '#666',
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'background 0.15s, color 0.15s',
    borderBottom: showDivider ? '0.5px solid #ECECEC' : undefined,
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.background = isLogout
      ? '#fef2f2'
      : '#f7f7f7';
    if (!isLogout) {
      (e.currentTarget as HTMLElement).style.color = '#1a1a2e';
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.background = 'transparent';
    if (!isLogout) {
      (e.currentTarget as HTMLElement).style.color = '#666';
    }
  };

  if (href) {
    return (
      <Link href={href} passHref>
        <a
          style={itemStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={onClick}
        >
          {icon}
          {label}
        </a>
      </Link>
    );
  }

  return (
    <div
      style={itemStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
    >
      {icon}
      {label}
    </div>
  );
};

export default Navbar;
